<template>
  <aside
    class="flex min-w-0 flex-col border-l border-gray-200 bg-white"
    role="dialog"
    :aria-label="record?.label || 'Result preview'"
    data-cy="conflict-preview-panel"
  >
    <!-- Just the close control: the record's name is the first row of its own box,
         and repeating it in a header made the panel say everything twice. -->
    <div
      class="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-2"
    >
      <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
        {{ kindLabel }}
        <span v-if="record?.archived" class="ml-1.5 text-gray-400"
          >· Archived</span
        >
      </span>
      <LlSecondaryButton
        icon="mdi-close"
        variant="text"
        size="small"
        aria-label="Close preview"
        data-cy="conflict-preview-close"
        @click="emit('close')"
      />
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      <v-skeleton-loader
        v-if="loading"
        type="paragraph, list-item-two-line@4"
      />

      <div
        v-else-if="error"
        class="mt-4 rounded border-l-4 border border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs"
        data-cy="conflict-preview-error"
      >
        <span class="font-medium text-red-700"
          >This record could not be loaded.</span
        >
        The result above still stands — only the detail below is missing.
      </div>

      <template v-else-if="record">
        <section
          class="border-b border-gray-200 py-4"
          data-cy="conflict-preview-record-box"
        >
          <div class="mb-2.5 flex items-baseline justify-between gap-3">
            <h3
              class="text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              {{ kindLabel }}
            </h3>
            <a
              v-if="recordHref"
              :href="recordHref"
              target="_blank"
              rel="noopener noreferrer"
              class="shrink-0 text-xs text-blue-700 underline underline-offset-2 hover:text-blue-900"
              data-cy="conflict-preview-open-record"
            >
              Open {{ kindLabel }}
            </a>
          </div>
          <dl class="grid gap-x-3.5 gap-y-1.5 text-sm" :style="SUMMARY_COLUMNS">
            <template v-for="item in record.summary" :key="item.label">
              <dt class="text-gray-500">{{ item.label }}</dt>
              <dd class="m-0 [overflow-wrap:anywhere]">
                <span v-if="item.value">{{ item.value }}</span>
                <span v-else class="text-gray-400">—</span>
              </dd>
            </template>
          </dl>
        </section>

        <!-- A matter or a time entry is not the client, so the client gets its own
             box with the contact details the reviewer opened the row to find. -->
        <section
          v-if="record.client"
          class="border-b border-gray-200 py-4"
          data-cy="conflict-preview-client-box"
        >
          <div class="mb-2.5 flex items-baseline justify-between gap-3">
            <h3
              class="text-xs font-medium uppercase tracking-wider text-gray-500"
            >
              Client
            </h3>
            <a
              v-if="clientHref"
              :href="clientHref"
              target="_blank"
              rel="noopener noreferrer"
              class="shrink-0 text-xs text-blue-700 underline underline-offset-2 hover:text-blue-900"
              data-cy="conflict-preview-open-client"
            >
              Open Client
            </a>
          </div>
          <dl class="grid gap-x-3.5 gap-y-1.5 text-sm" :style="SUMMARY_COLUMNS">
            <template v-for="item in record.client.details" :key="item.label">
              <dt class="text-gray-500">{{ item.label }}</dt>
              <dd class="m-0 [overflow-wrap:anywhere]">
                <span v-if="item.value">{{ item.value }}</span>
                <span v-else class="text-gray-400">—</span>
              </dd>
            </template>
          </dl>
        </section>

        <!--
          Two columns, no field colours: the pills carry colour in the results, and
          repeating them down a nine-row list turns the panel into a paint chart. The
          highlight on the value is the only thing marking a match.
        -->
        <section class="py-4">
          <h3
            class="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500"
          >
            Every field this check searched
          </h3>
          <p class="mb-2.5 text-xs leading-relaxed text-gray-500">
            A field with no value was still read.
          </p>
          <dl
            class="grid items-baseline gap-x-3.5 gap-y-1.5 text-sm"
            :style="FIELD_COLUMNS"
            data-cy="conflict-preview-fields"
          >
            <template v-for="field in record.searchedFields" :key="field.label">
              <dt class="text-gray-500">{{ fieldLabel(field) }}</dt>
              <dd class="m-0 leading-relaxed [overflow-wrap:anywhere]">
                <span
                  v-if="field.unavailable"
                  class="italic text-gray-400"
                  data-cy="conflict-preview-unavailable"
                >
                  searched, but not returned by this record
                </span>
                <span v-else-if="!field.value" class="text-gray-400">—</span>
                <template v-else>
                  <!-- ll-standards-ignore LL091 -- static list: tokens are derived
                       from one field value, never inserted into or reordered -->
                  <template
                    v-for="(token, index) in tokensFor(field)"
                    :key="index"
                  >
                    <mark
                      v-if="token.matched"
                      class="bg-yellow-200 px-0.5 text-yellow-900"
                      >{{ token.text }}</mark
                    >
                    <template v-else>{{ token.text }}</template>
                  </template>
                </template>
              </dd>
            </template>
          </dl>
          <p class="mt-3 text-xs leading-relaxed text-gray-500">
            Time entry descriptions on this record:
            <span class="font-medium text-gray-700">
              {{ includedTimeEntries ? 'searched' : 'not searched' }} </span
            >. Invoice narratives are never searched.
          </p>
        </section>
      </template>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { highlightTokens } from '../utils/conflictHighlightHelpers';
import { conflictFieldLabel } from '../constants/conflictFieldConstants';
import type {
  ConflictPreviewField,
  ConflictPreviewRecord,
} from '../models/conflictTypes';

const props = defineProps<{
  record: ConflictPreviewRecord | null;
  loading: boolean;
  error: unknown;
  includedTimeEntries: boolean;
  /** Built by the page from ui-router, so they are always internal URLs. */
  clientHref?: string | null;
  matterHref?: string | null;
}>();

const emit = defineEmits<{ close: [] }>();

/** Inline, not Tailwind arbitrary values — see the comment on ConflictCheckPage. */
const SUMMARY_COLUMNS = { gridTemplateColumns: 'auto minmax(0, 1fr)' };
const FIELD_COLUMNS = {
  gridTemplateColumns: 'minmax(110px, auto) minmax(0, 1fr)',
};

const KIND_LABELS: Record<string, string> = {
  client: 'Client',
  matter: 'Matter',
  timeEntry: 'Time entry',
};

const kindLabel = computed(() =>
  props.record ? KIND_LABELS[props.record.type] ?? 'Record' : 'Record',
);

/** The record's own link: a matter opens the matter, a client opens the client. */
const recordHref = computed(() =>
  props.record?.type === 'matter' ? props.matterHref : props.clientHref,
);

const fieldLabel = (field: ConflictPreviewField) => conflictFieldLabel(field.id);

function tokensFor(field: ConflictPreviewField) {
  return highlightTokens(field.value, field.matchedWords);
}
</script>
